import { type JSON_FORM } from '#app/constants/forms.ts'
import { DynamicQuestionComponent } from '#app/features/form/fields/dynamicQuestionComponent.tsx'

import { getCurrentStepSchema } from '#app/features/form/wizard/utils/getCurrentStepSchema.ts'
import {
	type GetStepsProps,
	type Steps,
	type RegisterFormHook,
	type BackendActions,
} from '#types/form/wizard.ts'

interface GenerateDynamicStepsFromDataProps {
	exemptions: string[]
}

export function generateDynamicStepsFromData({
	exemptions,
}: GenerateDynamicStepsFromDataProps) {
	return ({
		answeredQuestionsStack,
		questionTree,
		editableParents,
	}: GetStepsProps<typeof JSON_FORM>) => {
		return (criteria: {
			id: string
			needsToBeIncludedByController?: boolean
			isController?: boolean

			backendAction: BackendActions
		}) => {
			const dynamicSteps: Steps<any>[] = []

			questionTree.forEach(treeItem => {
				if (exemptions.includes(treeItem.id)) return

				if (criteria.id && !treeItem.id.includes(criteria.id)) return

				const currentQuestionId =
					answeredQuestionsStack.find(({ id }) => id === treeItem.id)
						?.currentQuestionId ?? 'A1'

				dynamicSteps.push({
					id: treeItem.id,
					isInJsonTree: true,
					needsToBeIncludedByController: criteria.needsToBeIncludedByController
						? true
						: false,
					fieldKey: `${treeItem.id}-${currentQuestionId}`,
					validator: getCurrentStepSchema(
						`${treeItem.id}-${currentQuestionId}`,
					),
					questionText: Object.keys(treeItem.tree.questions).map(questionId => {
						return {
							id: `${treeItem.id}-${questionId}`,
							text: treeItem.tree.questions[questionId].text,
						}
					}),

					isController: false,
					hasBackendCall: true,
					backendAction: criteria.backendAction,
					component: ({
						register,
						getValues,
						dispatch,
						watch,
						control,
					}: RegisterFormHook<any>) => {
						return (
							<DynamicQuestionComponent
								canBeParent
								register={register}
								dispatch={dispatch}
								getValues={getValues}
								watch={watch}
								control={control}
								key={currentQuestionId as string}
								fieldKey={`${treeItem.id}-${currentQuestionId}`}
								editableParents={editableParents}
								questionTree={questionTree}
								answeredQuestionsStack={answeredQuestionsStack}
								currentQuestionTreeId={treeItem.id}
							/>
						)
					},
				})
			})

			return dynamicSteps
		}
	}
}
